// What the Assembly panel plays, built in the browser out of the takes in
// takeStore with the shared montage (src/shared/assembly.js) — the same
// functions Accept runs on the server, on the same timings, on samples
// decoded to the same numbers. Nothing waits for the server here: a click
// changes the Draft's state, the turn is rebuilt from memory in milliseconds.
//
// buildDraftPreview(v, state) -> the shape the panel renders:
//   {rough, measuring, warnings,
//    turn: {url, duration, seams, placed, cuts, removedSec, pauseCuts, gain, summary},
//    base | source | activeTurn: {versionId, start, end}  (the same turn in those takes)
//    spliced: {url, start, end, turnStart, turnEnd}       (the active take with the Draft in it)
//   }
// `rough` names the takes still without timings (`measuring`): nothing is
// shown as exact then, and the caller asks the server to put them in the
// aligner's line. A url is a blob: URL the caller releases when replaced.

import { takeSamples, takeAlignment, wavUrl, RATE } from './takeStore';
import { composeTurn, spliceTurnInto, baseRangeFor, assemblySummary, CONTEXT_SEC } from '../../shared/assembly';
import { trimTo } from '../../shared/sampleOps';

const round3 = (x) => Math.round(x * 1000) / 1000;

// The takes a state's assembly reads: the base and every segment's take.
export function takesOfState(state) {
  return [...new Set([Number(state.base.versionId), ...(state.segments || []).map((s) => Number(s.versionId))])];
}

// Which of these takes have timings. -> {aligns: Map, missing: [versionId]}
export async function alignmentsOf(ids) {
  const aligns = new Map();
  const missing = [];
  await Promise.all(ids.map(async (id) => { const al = await takeAlignment(id); if (al) aligns.set(id, al); else missing.push(id); }));
  return { aligns, missing };
}

export async function buildDraftPreview(v, state, { withContext = true } = {}) {
  const t = v.turn;
  const loc = { body: t.body, bodyStart: t.bodyStart, bodyEnd: t.bodyEnd != null ? t.bodyEnd : t.bodyStart + t.body.length, changed: t.changed };
  const active = v.active ? Number(v.active.versionId) : null;
  const ids = takesOfState(state);
  const want = active && !ids.includes(active) ? ids.concat([active]) : ids;
  const { aligns, missing } = await alignmentsOf(want);
  if (missing.length) return { rough: true, measuring: missing, turn: null, spliced: null, warnings: [], base: null, source: null, activeTurn: null };
  const decoded = new Map();
  await Promise.all(ids.map(async (id) => { decoded.set(id, (await takeSamples(id)).samples); }));
  const asm = composeTurn({
    rate: RATE, text: t.fieldText, loc, state,
    samplesOf: (id) => decoded.get(id), alignmentOf: (id) => aligns.get(id) || null, turnName: t.label,
  });
  const sameTurnIn = async (versionId) => {
    if (!versionId) return null;
    const al = aligns.get(Number(versionId)) || await takeAlignment(versionId);
    if (!al) return null;
    try { const r = baseRangeFor(t.fieldText, loc.bodyStart, loc.bodyEnd, al); return { versionId: Number(versionId), start: round3(r.a), end: round3(r.b) }; } catch (e) { return null; }
  };
  const out = {
    rough: false, measuring: [], warnings: asm.warnings,
    turn: { url: wavUrl(asm.samples, RATE), duration: asm.duration, seams: asm.seams, placed: asm.placed, cuts: asm.cuts, removedSec: asm.removedSec, pauseCuts: asm.pauseCuts, gain: asm.gain, summary: assemblySummary(asm) },
    base: await sameTurnIn(state.base.versionId),
    source: v.draft.sourceVersionId ? await sameTurnIn(v.draft.sourceVersionId) : null,
    activeTurn: null,
    spliced: null,
  };
  if (active && withContext) {
    const alA = aligns.get(active);
    const baseA = (await takeSamples(active)).samples;
    const into = spliceTurnInto({ rate: RATE, text: t.fieldText, loc, assembled: asm, activeSamples: baseA, activeAlign: alA });
    const from = round3(Math.max(0, into.range.a - CONTEXT_SEC));
    const toAfter = Math.min(into.samples.length / RATE, into.newEnd + CONTEXT_SEC);
    const cut = trimTo(into.samples, RATE, from, toAfter);
    out.activeTurn = { ...(await sameTurnIn(active)), inContext: { versionId: active, start: from, end: round3(into.range.b + CONTEXT_SEC) } };
    out.spliced = { url: wavUrl(cut, RATE), start: 0, end: round3(cut.length / RATE), turnStart: round3(into.range.a - from), turnEnd: round3(into.newEnd - from) };
  } else if (active) {
    out.activeTurn = await sameTurnIn(active);
  }
  return out;
}

// The picked words in every source take: the seconds they cover, or why the
// take cannot lend them (the server's placeIn, in the browser).
export async function placeInSources(v, sources, snapped) {
  const t = v.turn;
  const out = [];
  await Promise.all(sources.map(async (s, i) => {
    if (!s.hasTimings) { out[i] = { ...s, ok: false, why: 'no timings yet' }; return; }
    try {
      const al = await takeAlignment(s.versionId);
      if (!al) { out[i] = { ...s, ok: false, why: 'no timings yet' }; return; }
      const r = baseRangeFor(t.fieldText, t.bodyStart + snapped.start, t.bodyStart + snapped.end, al);
      out[i] = { ...s, ok: true, start: round3(r.a), end: round3(r.b), wordStart: r.a, wordEnd: r.b, missing: r.missing };
    } catch (e) { out[i] = { ...s, ok: false, why: e.message }; }
  }));
  return { range: snapped, sources: out };
}
